import * as React from 'react'
import { Button } from 'primereact/button'
import { InputText, InputTextProps } from 'primereact/inputtext'
import { Dialog } from 'primereact/dialog'
import { Calendar, CalendarBaseProps } from 'primereact/calendar'
import { addLocale } from 'primereact/api'
import Icon from './Icon'
import { InputProps } from './InputTypes'
import moment from 'moment'

export type InputCalendarProps = Omit<
    InputProps,
    'value' | 'onChange' | 'onClear' | 'onFocus' | 'optionRightElement'
> & {
    /**
     * true인 경우 과거 조회 용으로 '오늘' 혹은 today 이후로 선택 못하게 함.
     */
    isPastOnly?: boolean
    /**
     * 선택한 날짜, 없는 경우 today or 로컬 시간
     */
    value?: Date
    /**
     * 특정 날짜를 오늘로 지정하는 경우 사용
     */
    today?: Date
    /**
     * 날짜를 변경하면 발생하는 콜백
     */
    onChange?: (value?: Date) => void
    /**
     * 페이지에 바로 보여주는 ui 사용 시 true
     */
    isIsolated?: boolean
    /**
     * 최소 선택 가능한 날짜
     */
    minDate?: Date
    /**
     * 최대 선택 가능한 날짜
     */
    maxDate?: Date
    /**
     * input에 추가로 설정할 Props
     */
    inputProps?: Omit<InputTextProps, 'value' | 'onChange'>
    /**
     * calrendar에 추가로 설정할 Props(minDate, maxDate는 무시됨)
     */
    calrendarProps?: CalendarBaseProps
}

const CompCalendar: React.FC<InputCalendarProps> = (props) => {
    let {
        inputProps,
        calrendarProps,
        minDate = new Date(1950, 0, 1),
        maxDate,
        ...other
    } = props
    const {
        isPastOnly = false,
        today,
        id,
        value,
        label = '',
        placeholder = '',
        title = '',
        helpId = '',
        helpMsg = '',
        result = '',
        resultId = '',
        resultMsg = '',
        disabled = false, //disabled 핸들
        readOnly = false, //readOnly 핸들
        className = '',
        onChange,
        isIsolated = false,
        ...rest
    } = other

    if (isPastOnly) {
        const preMaxDate = today ?? new Date()
        if (maxDate) {
            //오늘과 maxDate 중에서 작은 날짜로 시정한다.
            maxDate = new Date(Math.min(Number(preMaxDate), Number(maxDate)))
        } else {
            maxDate = today ?? new Date()
        }
    } else {
        // maxDate = new Date(2025, 12, 31) //이거 문제있는데?

        //maxDate 있으면 셋팅하고 아니면 3년뒤 마지막날짜
        maxDate = maxDate ?? moment().add(3, 'year').endOf('year').toDate()
    }

    addLocale('ko', {
        firstDayOfWeek: 0,
        dayNames: [
            '일요일',
            '월요일',
            '화요일',
            '수요일',
            '목요일',
            '금요일',
            '토요일',
        ],
        dayNamesShort: ['일', '월', '화', '수', '목', '금', '토'],
        dayNamesMin: ['일', '월', '화', '수', '목', '금', '토'],
        monthNames: [
            '1월',
            '2월',
            '3월',
            '4월',
            '5월',
            '6월',
            '7월',
            '8월',
            '9월',
            '10월',
            '11월',
            '12월',
        ],
        monthNamesShort: [
            '1월',
            '2월',
            '3월',
            '4월',
            '5월',
            '6월',
            '7월',
            '8월',
            '9월',
            '10월',
            '11월',
            '12월',
        ],
        today: '오늘',
        clear: '지우기',
        dateFormat: 'yy.mm.dd',
    })

    const calendarRef = React.useRef<HTMLDivElement>(null)
    const observerRef = React.useRef<MutationObserver>(null)
    const [visible, setVisible] = React.useState(false)
    const [selectedDate, setSelectedDate] = React.useState<Date>(value)

    React.useEffect(() => {
        if (value) {
            setSelectedDate(value)
        }
    }, [value])

    // 날짜선택시
    const handleDateSelect = (date: Date | null) => {
        if (date) {
            setSelectedDate(date)
            onChange?.(date)
            setVisible(false)

            domChangeDetect() // 변경감지
        }
    }

    // 년,월 타이틀 순서변경
    const calendarTitleSort = React.useCallback(() => {
        const title = calendarRef.current?.querySelector('.p-datepicker-title')
        if (title) {
            const year = title.querySelector('.p-datepicker-year')
            const month = title.querySelector('.p-datepicker-month')
            if (year !== null && month !== null) {
                year.setAttribute('aria-label', `${year.textContent}년`)
                month.setAttribute('aria-label', month.textContent)
            }
            if (month && year && month.nextElementSibling === year) {
                title.insertBefore(year, month)
            }
        }
    }, [title])

    // header 토,일 색지정
    const tableHeaderWeek = () => {
        const headers = calendarRef.current?.querySelectorAll(
            '.p-datepicker-calendar th span'
        )
        headers.forEach((item) => {
            const text = item.textContent?.trim() || ''
            if (text === '토') item.classList.add('saturday')
            if (text === '일') item.classList.add('sunday')
        })
    }

    // body 토,일 표시 색지정
    const customDayTemplate = (obj) => {
        let date = new Date(obj.year, obj.month, obj.day)
        if (date.getDay() === 0) {
            return <span className="sunday">{obj.day}</span>
        } else if (date.getDay() === 6) {
            return <span className="saturday">{obj.day}</span>
        } else {
            return obj.day
        }
    }

    // 접근성
    const pickerA11ySetup = () => {
        const dayLabels = calendarRef.current?.querySelectorAll(
            '[data-pc-section="daylabel"]'
        )
        dayLabels.forEach((item) => item.setAttribute('role', 'button'))
        const btnPrev = calendarRef.current?.querySelector('.p-datepicker-prev')
        btnPrev.setAttribute('aria-label', '이전달')
        const btnNext = calendarRef.current?.querySelector('.p-datepicker-next')
        btnNext.setAttribute('aria-label', '다음달')
    }

    // dialog 안으로 포함버전 초기설정
    const dialogSetup = () => {
        /**
         * 달력이 다이얼로그에서 표시되는 구조라 추가함.
         */
        if (calrendarProps && 'onVisibleChange' in calrendarProps) {
            calrendarProps.onVisibleChange(undefined)
        }

        calendarTitleSort()
        tableHeaderWeek()
        pickerA11ySetup()
    }

    // dom변경시
    const domChangeDetect = () => {
        const observer = new MutationObserver(() => {
            calendarTitleSort()
            tableHeaderWeek()
            pickerA11ySetup()
        })
        observer.observe(calendarRef.current, {
            childList: true,
            subtree: true,
        })
        observerRef.current = observer
        return () => observer.disconnect()
    }

    let helpMsgCheck = null
    if (!(result !== 'hidden' && resultMsg) && helpMsg) {
        helpMsgCheck = (
            <p id={helpId} className="p-msg-help">
                {helpMsg}
            </p>
        )
    }

    let resultCheck = null
    if (result !== 'hidden' && resultMsg) {
        resultCheck = (
            <p id={resultId} className={`p-msg-${result}`}>
                {resultMsg}
            </p>
        )
    }

    React.useEffect(() => {
        if (isIsolated) domChangeDetect()
    }, [])

    const inputText = React.useMemo(() => {
        if (selectedDate) {
            return calrendarProps?.dateFormat
                ? moment(selectedDate).format(calrendarProps.dateFormat)
                : selectedDate.toLocaleDateString()
        } else {
            return ''
        }
    }, [selectedDate])

    return (
        <>
            {isIsolated ? (
                <div ref={calendarRef}>
                    <Calendar
                        {...calrendarProps}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChange={(e) => {
                            handleDateSelect(e.value)
                        }}
                        // 아래는 외부 값과 상관없이 고정됨.
                        value={value}
                        locale="ko"
                        dateTemplate={customDayTemplate}
                        inline
                    />
                </div>
            ) : (
                <>
                    <div
                        className={`input-basic ${className} ${
                            result === 'error' ? '-error' : ''
                        }`.trim()}
                    >
                        {label && <label htmlFor={id}>{label}</label>}
                        <div className="input">
                            <InputText
                                {...inputProps}
                                // 아래는 고정
                                type="text"
                                id={id}
                                placeholder={placeholder}
                                title={title}
                                aria-describedby={`${helpId} ${resultId}`.trim()}
                                disabled={disabled}
                                readOnly={readOnly}
                                onClick={() => setVisible(true)}
                                value={inputText}
                            />
                            <div className="option-right">
                                <Button onClick={() => setVisible(true)}>
                                    <Icon name="calendar"></Icon>
                                </Button>
                            </div>
                        </div>
                        {helpMsgCheck}
                        {resultCheck}
                    </div>
                    <Dialog
                        draggable={false}
                        header={<span tabIndex={0}>{title}</span>}
                        visible={visible}
                        onShow={() => {
                            dialogSetup()
                            domChangeDetect()
                        }}
                        onHide={() => setVisible(false)}
                        closable={true}
                        ariaCloseIconLabel={`${title} 창닫기`}
                        dismissableMask
                        className="bottom-sheet"
                    >
                        <div ref={calendarRef}>
                            <Calendar
                                {...calrendarProps}
                                minDate={minDate}
                                maxDate={maxDate}
                                onChange={(e) => {
                                    handleDateSelect(e.value)
                                }}
                                // 아래는 외부 값과 상관없이 고정됨.
                                value={value}
                                locale="ko"
                                dateTemplate={customDayTemplate}
                                inline
                            />
                        </div>
                    </Dialog>
                </>
            )}
        </>
    )
}

export default CompCalendar
